import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

export default function AIForRVParks() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  const faqs = [
    {
      question: 'How can AI help an RV park or campground specifically?',
      answer: 'RV parks and campgrounds receive a constant stream of repetitive inquiries — availability, site types, hookup options, pet policies, nearby attractions, check-in procedures. Our AI handles all of these automatically by phone, chat, or text, freeing your staff from fielding the same questions dozens of times a day while ensuring every guest gets a fast, accurate answer.'
    },
    {
      question: 'Can the AI handle reservation and availability inquiries?',
      answer: 'Yes. Your AI can answer questions about availability, site types (full hookup, water/electric, dry camping), rates, and seasonality. It can guide guests through your reservation process and book appointments or direct them to your online booking system — all without your staff picking up the phone.'
    },
    {
      question: 'What about after-hours calls from guests needing help on-site?',
      answer: 'After-hours guest support is one of the biggest pain points for campground owners. Your AI answers those late-night calls, handles common on-site issues (directions to restrooms, quiet hours reminders, check-in instructions), and escalates true emergencies to the right contact — so your staff isn\'t woken up for non-urgent issues.'
    },
    {
      question: 'Can the AI handle different inquiries based on the season or upcoming events?',
      answer: 'Absolutely. You can update your AI with seasonal information — rally weekends, special events, current availability constraints, upcoming closures — so it always gives guests accurate, timely information without you manually answering every related call.'
    },
    {
      question: 'Is this useful for a small campground, or just large RV parks?',
      answer: 'It\'s especially valuable for small owner-operated parks. Large parks have staff to spread the call volume. Small parks often have just the owner or one staff member handling everything. AI gives a small park the customer-service capacity of a much larger operation without the overhead.'
    },
    {
      question: 'How does pricing work for an RV park or campground?',
      answer: 'Our pricing is a flat monthly subscription with no per-call fees. Most parks find that even a 10% reduction in lost reservations from missed after-hours calls more than pays for the entire service. We offer a free demo so you can see the value before committing.'
    },
  ];

  return (
    <>
      <SEO
        title="AI for RV Parks & Campgrounds | 24/7 Guest Support & Reservation Assistance"
        description="Give your RV park or campground the customer service of a large resort. Our AI answers guest calls 24/7, handles reservation inquiries, and manages after-hours support automatically."
        canonical="/ai-for-rv-parks"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Receptionist for RV Parks and Campgrounds',
              description: 'AI-powered guest call handling, reservation inquiry management, and after-hours support for RV parks and campgrounds.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative pt-32 pb-20 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="/readdy-assets/rv-parks-hero-001.jpg"
              alt="AI for RV Parks and Campgrounds"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/70 to-white/95"></div>

          <div className="relative max-w-4xl mx-auto text-center">
            <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Built for RV Parks &amp; Campgrounds
            </span>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              AI for RV Parks &amp; Campgrounds:<br />World-Class Guest Service, Automatically
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Your guests have questions at all hours — availability, site types, hookup options, pet policies, check-in times. Our AI answers every call and message 24/7, handles reservation inquiries, and gives guests the instant, helpful experience that keeps them coming back.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get a Free Demo
            </button>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* The Problem */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                The Challenge of Running an RV Park or Campground
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Running an RV park or campground is a demanding hospitality business. Guests call or message at all hours — sometimes from the road, sometimes at 11pm trying to confirm their check-in. They want to know about site availability, hookup types, pull-through vs. back-in, monthly rates, pet rules, and a hundred other things. Our <Link to="/ai-phone-answering-service" className="text-primary hover:underline font-medium">AI phone answering service</Link> handles every one of these inquiries automatically.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                For most small-to-mid-sized parks, this all falls on one or two people. The same questions get answered over and over. After-hours calls go to voicemail and prospective guests book somewhere else. Your staff spends hours each day on repetitive phone calls instead of taking care of guests who are already on-site. An <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI receptionist</Link> handles these calls so your team doesn't have to.
              </p>
              <p className="text-gray-700 leading-relaxed">
                AI for RV parks and campgrounds changes this entirely. Your AI assistant is your always-available front desk — answering questions, handling reservation inquiries, providing on-site support, and giving every guest the feeling that your park is well-staffed and attentive, even at 2 in the morning.
              </p>
            </div>

            {/* What We Do */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                How Our AI Serves Your RV Park or Campground
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We combine <Link to="/ai-phone-answering-service" className="text-primary hover:underline font-medium">24/7 AI phone answering</Link>, an <Link to="/services/ai-chatbot" className="text-primary hover:underline font-medium">intelligent website chatbot</Link>, and a full <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI receptionist</Link> into one seamless guest experience platform — trained specifically on your park.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: 'ri-phone-line',
                    title: '24/7 Guest Call Handling',
                    body: 'Guests call at all hours when they\'re on the road, planning a trip, or dealing with an issue on-site. Your AI answers immediately with your park\'s name, answers their questions using your park\'s specific details — site types, amenities, rates, policies — and handles or routes their request appropriately. No more voicemail, no more missed reservations.'
                  },
                  {
                    icon: 'ri-question-answer-line',
                    title: 'Automated FAQ Handling for Common Guest Questions',
                    body: 'What\'s your check-in time? Do you have full hookups? Are big rigs allowed? Is there a dog park? Do you have Wi-Fi? Your AI knows the answers to every question about your park and delivers them instantly — eliminating the most repetitive drain on your staff\'s time.'
                  },
                  {
                    icon: 'ri-moon-line',
                    title: 'After-Hours On-Site Guest Support',
                    body: 'Late-night calls from guests needing directions to the dump station, questions about quiet hours, or needing the gate code don\'t have to wake up your manager. Your AI handles these gracefully and escalates only the calls that genuinely require human intervention.'
                  },
                  {
                    icon: 'ri-calendar-check-line',
                    title: 'Reservation Inquiry Assistance',
                    body: 'When guests call or chat about making a reservation, your AI walks them through the process, answers availability questions, explains site types and pricing, and guides them to your booking system or collects their information for a callback — capturing every lead with zero effort from your team.'
                  },
                ].map(item => (
                  <div key={item.title} className="bg-gray-50 p-6 rounded-lg flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits at a Glance */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What RV Park Owners Gain With AI
              </h2>
              <div className="grid md:grid-cols-2 gap-5">
                {[
                  { icon: 'ri-time-line', title: 'Hours Back Every Week', body: 'Eliminate the most repetitive part of your job — answering the same questions over and over. Your AI handles it so you can focus on the guest experience that actually matters.' },
                  { icon: 'ri-money-dollar-circle-line', title: 'Fewer Lost Reservations', body: 'After-hours callers who can\'t get an answer book somewhere else. Your AI ensures every prospective guest gets a helpful response, even at midnight on a Saturday.' },
                  { icon: 'ri-star-line', title: 'Better Guest Reviews', body: 'Fast, helpful responses are the #1 driver of positive reviews. Guests who feel attended to — even through AI — leave better reviews and recommend your park to others.' },
                  { icon: 'ri-settings-3-line', title: 'Easy to Customize & Update', body: 'Seasonal changes, new amenities, special events? Update your AI in minutes and every caller gets the latest information — no staff retraining required.' },
                ].map(item => (
                  <div key={item.title} className="border border-gray-200 rounded-lg p-6 flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-primary/10 rounded-lg">
                      <i className={`${item.icon} text-xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-16 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Complete AI Suite for Hospitality &amp; Outdoor Recreation
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { to: '/services/ai-receptionist', icon: 'ri-customer-service-2-line', title: 'AI Receptionist', desc: 'Full virtual front desk — calls, texts, and website chat in one place.' },
                  { to: '/ai-phone-answering-service', icon: 'ri-phone-line', title: 'Phone Answering Service', desc: 'Professional AI that answers every call in your park\'s name, 24/7.' },
                  { to: '/services/ai-chatbot', icon: 'ri-chat-smile-3-line', title: 'Website Chatbot', desc: 'Capture website visitors and answer reservation questions automatically.' },
                  { to: '/ai-appointment-scheduling', icon: 'ri-calendar-2-line', title: 'Appointment Scheduling', desc: 'Let guests book tours or callbacks automatically through your AI.' },
                ].map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="bg-white p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Other Industries */}
            <div className="mb-16">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                We Also Help These Small Businesses
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/ai-for-contractors" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Contractors</Link>
                <Link to="/ai-for-roofers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Roofers</Link>
                <Link to="/ai-for-landscapers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Landscapers</Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">Ready to Upgrade Your Guest Experience?</h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Book a free 15-minute demo and see how our AI fits your RV park or campground. We handle all the setup — you just start answering fewer repetitive calls.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Schedule My Free Demo
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />
        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
